import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({ user: null, loading: true });

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔐 AuthContext: Initializing auth state listener');
    
    // Check current auth state immediately
    const currentUser = auth.currentUser;
    console.log('🔐 AuthContext: Initial current user:', currentUser?.uid || 'null');
    
    // Set a shorter timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.warn('🔐 AuthContext: Auth state check timed out, setting loading to false');
      setUser(auth.currentUser);
      setLoading(false);
    }, 2000); // 2 second timeout

    try {
      // Wait for Firebase to initialize
      const unsubscribe = onAuthStateChanged(auth, 
        (user) => {
          console.log('🔐 AuthContext: Auth state changed', { user: user?.uid || 'null' });
          clearTimeout(timeout);
          setUser(user);
          setLoading(false);
        },
        (error) => {
          console.error('🔐 AuthContext: Auth state change error:', error);
          clearTimeout(timeout);
          setUser(auth.currentUser);
          setLoading(false);
        }
      );

      return () => {
        clearTimeout(timeout);
        unsubscribe();
      };
    } catch (error) {
      console.error('🔐 AuthContext: Error setting up auth listener:', error);
      clearTimeout(timeout);
      setUser(auth.currentUser);
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}